import React, { useEffect, useState } from 'react';
import { Phone, Check } from 'lucide-react';
import { baseApi, BaseApiError } from '../../../api/baseClient';
import { useError } from '../../../context/ErrorContext';

// País del teléfono del kiosko (F2 países, 20-ago-2026). Autocontenida a propósito, calcando
// IntegratedQRCodePanel: carga de GET /loyalty/settings y persiste sola en tenant_settings.kiosk
// vía PUT /biz/settings (el backend valida el catálogo y hace MERGE — no pisa el candado legado).
// Catálogo soportado = solo MX y US: fuera de ellos el backend no sabe armar el WhatsApp
// (plan F4 en forobeta-ops/docs/lealify-paises-telefono-plan-20260820.md).
const CATALOG = [
  { code: '+52', flag: '🇲🇽', name: 'México' },
  { code: '+1', flag: '🇺🇸', name: 'Estados Unidos' },
];

const PhoneCountrySection: React.FC = () => {
  const { addError } = useError();
  const [countries, setCountries] = useState<string[]>(['+52', '+1']);
  const [defaultCountry, setDefaultCountry] = useState<string>('+52');
  const [locked, setLocked] = useState<string | null>(null);   // candado legado: se muestra, no se edita
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    baseApi.get<{ kioskCountries?: string[] | null; kioskDefaultCountry?: string | null; kioskCountryCode?: string | null }>('/loyalty/settings')
      .then((d) => {
        if (d.kioskCountries?.length) setCountries(d.kioskCountries.filter((c) => CATALOG.some((k) => k.code === c)));
        if (d.kioskDefaultCountry) setDefaultCountry(d.kioskDefaultCountry);
        setLocked(d.kioskCountryCode || null);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));   // sin carga no bloqueamos la página; el form usa defaults
  }, []);

  const persist = async (next: string[], nextDefault: string) => {
    setSaving(true);
    setSaved(false);
    try {
      await baseApi.put('/biz/settings', {
        settings: { kiosk: { countries: next, defaultCountry: nextDefault } },
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      const detail = error instanceof BaseApiError ? error.message : '';
      addError(detail ? `No se pudo guardar el país del teléfono: ${detail}`
        : 'No se pudo guardar el país del teléfono. Inténtalo de nuevo.', 'unknown', 'phone-country-save');
    } finally {
      setSaving(false);
    }
  };

  const toggleCountry = (code: string) => {
    let next = countries.includes(code) ? countries.filter((c) => c !== code) : [...countries, code];
    if (!next.length) return;                       // al menos un país siempre
    // El preseleccionado tiene que estar entre los ofrecidos.
    const nextDefault = next.includes(defaultCountry) ? defaultCountry : next[0];
    setCountries(next);
    setDefaultCountry(nextDefault);
    persist(next, nextDefault);
  };

  const changeDefault = (code: string) => {
    setDefaultCountry(code);
    persist(countries, code);
  };

  if (locked) {
    const c = CATALOG.find((k) => k.code === locked);
    return (
      <div className="mt-6 pt-6 border-t border-line">
        <h4 className="text-sm font-medium text-content flex items-center gap-2">
          <Phone className="h-4 w-4 text-content-3" /> País del teléfono
        </h4>
        <p className="mt-2 text-sm text-content-3">
          Este negocio tiene el kiosco fijado a {c ? `${c.flag} ${c.name}` : locked}. Para cambiarlo, contacta a soporte.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-6 pt-6 border-t border-line">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-content flex items-center gap-2">
          <Phone className="h-4 w-4 text-content-3" /> País del teléfono
        </h4>
        {saved && <span className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1"><Check className="h-3.5 w-3.5" /> Guardado</span>}
      </div>
      <p className="mt-1 text-sm text-content-3">
        Qué ladas ofrece el kiosco al pedir el WhatsApp y cuál sale preseleccionada.
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {CATALOG.map((c) => {
          const on = countries.includes(c.code);
          return (
            <button key={c.code} type="button" disabled={saving || (!loaded)}
              onClick={() => toggleCountry(c.code)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-sm transition-colors ${on
                ? 'border-primary/50 bg-primary/10 text-content font-medium'
                : 'border-line text-content-3 hover:bg-surface-2'}`}>
              <span>{c.flag}</span> {c.name} <span className="text-content-3">{c.code}</span>
              {on && <Check className="h-3.5 w-3.5 text-primary" />}
            </button>
          );
        })}
      </div>
      {countries.length > 1 && (
        <div className="mt-3 flex items-center gap-2 text-sm">
          <label className="text-content-3">Preseleccionado:</label>
          <select value={defaultCountry} disabled={saving}
            onChange={(e) => changeDefault(e.target.value)}
            className="rounded-md border-line bg-surface text-content text-sm py-1 pl-2 pr-7 focus:ring-primary focus:border-primary">
            {countries.map((code) => {
              const c = CATALOG.find((k) => k.code === code);
              return <option key={code} value={code}>{c ? `${c.flag} ${c.name} (${c.code})` : code}</option>;
            })}
          </select>
        </div>
      )}
    </div>
  );
};

export default PhoneCountrySection;
